import React, { useContext, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@arcteryx/components-icons";
import { SiteContext } from "@arcteryx/components-contexts";
import { Modal } from "@arcteryx/components-modal";
import { H1, ScreenSize } from "@arcteryx/components-typography";
import { Button } from "@arcteryx/components-button";

const SimpleHeader = ({
  env = "production",
  logoUrl = "/",
  outletUrl = "/",
  items = 1,
  cartIconCallback = useCallback(() => { }, []),
  flagIconCallback = useCallback(() => { }, []),
}) => {
  const { market, country, language } = useContext(SiteContext);
  const { t } = useTranslation("components-simple-header");

  const [isOpen, setIsOpen] = useState(false);

  const logoData = useMemo(
    () => ({
      production: {
        logo: "arcteryx-logo",
        mobile: "arcteryx-logo-no-text",
        width: "100",
        mobileWidth: "45",
        href: logoUrl,
      },
      outlet: {
        logo: "arcteryx-logo-outlet",
        mobile: "arcteryx-logo-outlet",
        width: "150",
        mobileWidth: "70",
        href: outletUrl,
      },
    }),
    [logoUrl, outletUrl]
  );

  const { logo, mobile, width, mobileWidth, href } = logoData[env];

  const handleLinkClick = useCallback((href) => {
    window.location.href = href;
  }, []);

  return (
    <div data-testid="simple-header">
      <HeaderWrapper data-testid="header-wrapper">
        <div></div>
        <LogoWrapper href={href} onClick={() => setIsOpen(true)} data-testid="logo-wrapper">
          <Icon type={logo} width={width} height="auto" title="Arcteryx Logo" />
        </LogoWrapper>
        <MobileLogoWrapper href={href} onClick={() => setIsOpen(true)} data-testid="mobile-logo-wrapper">
          <Icon type={mobile} width={mobileWidth} title="Arcteryx Logo" />
        </MobileLogoWrapper>
        <div>
          <CartIconWrapper onClick={cartIconCallback} data-testid="cart-icon-wrapper">
            {items !== 0 && <span>{items}</span>}
            <Icon type="cart" width="20px" />
          </CartIconWrapper>
          <IconWrapper onClick={flagIconCallback} data-testid="flag-icon-wrapper">
            <Icon type="flag-ca" width="20px" height="17px" />
          </IconWrapper>
        </div>
      </HeaderWrapper>
      <div data-testid="modal">
        <Modal modalIsShown={isOpen} isClosable={true} toggleModal={() => setIsOpen(!isOpen)}>
          <ModalWrapper>
            <H1>{t("You Are About To Leave Checkout")}</H1>
            <ButtonWrapper>
              <Button context="Subtle" style={{ width: "100%" }} onClick={() => handleLinkClick(href)}>
                {t("Continue")}
              </Button>
              <Button context="Standard" style={{ width: "100%" }} onClick={() => setIsOpen(false)}>
                {t("Stay")}
              </Button>
            </ButtonWrapper>
          </ModalWrapper>
        </Modal>
      </div>
    </div>
  );
};

SimpleHeader.propTypes = {
  env: PropTypes.oneOf(["production", "outlet"]),
  logoUrl: PropTypes.string,
  outletUrl: PropTypes.string,
  items: PropTypes.number,
  cartIconCallback: PropTypes.func,
  flagIconCallback: PropTypes.func,
};

/* Header styles */
const HeaderWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid #ccc;
  padding-bottom: 1rem;
  align-items: baseline;

  > div {
    flex: 1 1 33.33%;
    box-sizing: border-box;
    text-align: center;

    &:last-child {
      text-align: right;
    }
  }

  @media only screen and (max-width: ${ScreenSize.sm}px) {
    > div {
      flex: 0 0 auto;
      width: 50%;
      white-space: nowrap;
      text-align: left;

      &:first-child {
        display: none;
      }

      &:last-child {
        text-align: right;
      }
    }
  }
`;

const LogoWrapper = styled.div`
  display: block;

  @media only screen and (max-width: ${ScreenSize.sm}px) {
    display: none;
  }
`;

const MobileLogoWrapper = styled.div`
  display: none;

  @media only screen and (max-width: ${ScreenSize.sm}px) {
    display: block;
  }
`;

const IconWrapper = styled.span`
  padding-left: 1rem;

  &:hover {
    cursor: pointer;
  }
`;

const CartIconWrapper = styled.span`
  padding-left: 1rem;
  position: relative;

  > span {
    position: absolute;
    font-size: 11px;
    top: -12px;
    right: -6px;
  }

  &:hover {
    cursor: pointer;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: var(--space-green);
  display: flex;
  justify-content: space-between;
`;

const ModalWrapper = styled.div`
  text-align: center;
`;

export default SimpleHeader;
